import type { ReqventosWorkflowEnginePin } from "../../../../core/src/contracts/reqventos-workflow-engine-pin";
import type { IncomingWork, IncomingWorkCounts } from "../../../../core/src/review/incoming-work";
export type { IncomingWork, IncomingWorkCounts };
import type { ReqventosWorkflowResultRef } from "../../../../core/src/contracts/reqventos-workflow-result-ref";
export type WorkflowEnginePin = ReqventosWorkflowEnginePin;
export interface OwnerWorkflowSettings {
  tenant_id: string; version: number; default_pin: WorkflowEnginePin; can_update: boolean; single_agent_capability: boolean;
  options: { engine: "deterministic" | "single_agent"; available: boolean; reason: string | null; profile: WorkflowEnginePin | null }[];
}

// Domain types mirroring the real core-api responses (see core/src: auth, review, offer,
// delivery, thread, users controllers). These are the live shapes consumed by the UI — there is
// no mock layer. Field names match the API exactly (camelCase facts, snake_case decision keys).

export type Role = "operator" | "admin" | "owner";

export type TenantStatus = "active" | "suspended" | "archived";

export interface SessionUser {
  id: string;
  email: string;
  /** Optional display name; initials avatar falls back to email when absent. */
  name?: string | null;
  role: Role | string;
  /** Null for the cross-tenant owner; present for every tenant-bound account. */
  tenantId?: string | null;
  /** Stored name of the effective business; null outside a business context. */
  tenantDisplayName?: string | null;
  /** Lifecycle status of the session tenant (null when tenant-less). Non-active means the
   *  API rejects every mutation — the UI shows the read-only banner (#231). */
  tenantStatus?: TenantStatus | null;
}

export interface AccountProfile {
  id: string;
  email: string;
  name: string | null;
  role: Role | string;
  avatar: { kind: "initials" };
}

/** One business as the owner plane returns it (GET /api/owner/tenants). */
export interface TenantRow {
  id: string;
  slug: string;
  displayName: string;
  status: TenantStatus | string;
  plan: string;
  createdAt: string;
  /** The business's own send pause switch (#238); the env kill-switch sits above it. */
  sendEnabled: boolean;
  /** Whether the signed-in platform owner has joined this business as staff. */
  isMember: boolean;
}

/** POST /api/owner/tenants/:id/send-toggle — the resulting per-tenant send state (#238). */
export interface TenantSendState {
  env_enabled: boolean;
  admin_enabled: boolean;
  tenant_enabled: boolean;
  enabled: boolean;
}

/** Result of a lifecycle transition (suspend/resume/archive). */
export interface TenantLifecycleResult {
  id: string;
  slug: string;
  status: TenantStatus | string;
}

/** Per-tenant usage overview (GET /api/owner/tenants/:id/usage) — counts only, no content. */
export interface TenantUsage {
  tenant: { id: string; slug: string; displayName: string; status: TenantStatus | string };
  requests: { total: number; open: number };
  users: { total: number };
  messages: { total: number; inbound: number };
  sends: { total: number; byState: Record<string, number> };
  llmCalls: { total: number; last30d: number; byPurpose: Record<string, number> };
}

export interface Flags {
  ai_enabled: boolean;
  rule_proposal_ai_enabled: boolean;
  followup_send_enabled: boolean;
  followup_send_env_enabled?: boolean;
  followup_send_admin_enabled?: boolean;
  /** #238: the session tenant's own pause switch — null for a tenant-less owner session. */
  followup_send_tenant_enabled?: boolean | null;
  bp_export_enabled: boolean;
  external_lookup_enabled: boolean;
  rule_params_db_enabled: boolean;
}

/** GET /health, the part the console reads (#2350): which kind of host serves it. Public, so the
 *  sign-in screen of a dev host can already say that it is not the live system. */
export interface HostEnvironmentInfo {
  environment: "production" | "dev";
}

/** GET /api/config — one installation-wide fixed code ruleset for every role and tenant. */
export interface AppConfigInfo {
  ruleset_version: string;
  rules_authority: "code_fixed";
  flags: Flags;
  /** #239: effective per-tenant feature toggles — the SPA hides disabled surfaces. Null for a
   *  tenant-less owner session (treat null like "everything the owner plane governs elsewhere"). */
  features: Record<FeatureKey, boolean> | null;
  rule_parameters: RuleParameterRuntimeDiagnostic;
  /** #1618: the values a new request of this business executes (its owner-set values or the code
   *  defaults); null when its active set no longer validates. Absent from an older server. */
  rule_parameter_values?: RuleParamValues | null;
  /** #1989: the header logo of this business; null without one. Absent from an older server. */
  business_logo?: BusinessLogo | null;
}

/** #1989: the image a business shows in the console header in place of the product wordmark. */
export interface BusinessLogo {
  id: string;
  content_type: "image/png" | "image/jpeg";
  byte_size: number;
  width: number;
  height: number;
  /** Content-addressed and tenant-scoped; pass it through `withTenantContext` before use. */
  url: string;
}

// ---- registered rule values and delegated business editing ------------------

export type RuleParamKey =
  | "minWrittenOfferGuests"
  | "bbqOnlineShopMinGuests"
  | "bbqPaidServiceMinGuests"
  | "budgetMandatory"
  | "fieldGroupsCombined"
  | "weekdayFoodMinimumNetCents"
  | "sundayHolidayFoodMinimumNetCents"
  | "extrasFoodMinimumNetCents";

export interface RuleParamValues {
  minWrittenOfferGuests: number;
  bbqOnlineShopMinGuests: number;
  bbqPaidServiceMinGuests: number;
  budgetMandatory: boolean;
  fieldGroupsCombined: boolean;
  weekdayFoodMinimumNetCents?: number;
  sundayHolidayFoodMinimumNetCents?: number;
  extrasFoodMinimumNetCents?: number;
}

/** One registered parameter as the server's code registry defines it. */
export interface RuleParameterDefinition {
  key: RuleParamKey;
  value_type: "integer" | "boolean";
  default_value: number | boolean;
  min_inclusive?: number;
  max_inclusive?: number;
  unit?: "guests" | "boolean" | "eur_cents";
  step?: number;
  allowed_values?: boolean[];
  risk: "medium_high" | "high" | "critical" | string;
  invariant_ids: string[];
  description: { de: string; en: string };
}

/** GET/POST /api/owner/tenants/:id/rule-parameters (#1617). `version` 0 = the code defaults. */
export interface OwnerTenantRuleParameters {
  tenant: { id: string; slug: string; display_name: string; status: TenantStatus | string };
  editable: boolean;
  source: "code_defaults" | "tenant_set";
  set_id: string | null;
  version: number;
  values: RuleParamValues;
  rule_params_hash: string;
  code_ruleset_version: string;
  effective_rules_hash: string;
  defaults: RuleParamValues;
  parameters: RuleParameterDefinition[];
  invariants: { id: string; keys: RuleParamKey[] }[];
  history: {
    set_id: string;
    version: number;
    values: RuleParamValues;
    rule_params_hash: string;
    created_by_actor_id: string | null;
    created_by_actor: {
      display_name: string;
      role: string;
      plane: "platform" | "business";
    } | null;
    created_at: string;
  }[];
}

/** Owner delegation uses full replacement; omitted keys remain platform-controlled. */
export type RuleValueGrant = { editable: false } | { editable: true; min?: number; max?: number; step?: number };
export type RuleValueDelegation = Partial<Record<RuleParamKey, RuleValueGrant>>;
export interface OwnerRuleValueDelegation {
  tenant_id: string;
  id: string | null;
  version: number;
  keys: RuleValueDelegation;
  history: {
    id: string;
    version: number;
    keys: RuleValueDelegation;
    created_by_actor_id: string | null;
    actor_role: string;
    plane: "platform" | "business";
    created_by_actor: OwnerTenantRuleParameters["history"][number]["created_by_actor"];
    created_at: string;
  }[];
}
export interface UpdateRuleValueDelegationInput {
  expected_version: number;
  keys: RuleValueDelegation;
}
export interface RuleDelegationIssue { code: string; key?: string; reason?: string }

/** POST body: a partial patch plus the version the owner edited. */
export interface UpdateTenantRuleParametersInput {
  expected_version: number;
  values: Partial<RuleParamValues>;
}

export interface BusinessRuleValues extends OwnerTenantRuleParameters {
  delegation: OwnerRuleValueDelegation;
}

export interface UpdateBusinessRuleValuesInput extends UpdateTenantRuleParametersInput {
  expected_delegation_version: number;
}

/** Read-only threshold comparison; unavailable counts must never be displayed as zero. */
export interface RuleValuePreview {
  tenant_id: string;
  version: number;
  delegation_version: number | null;
  values: RuleParamValues;
  changed_keys: RuleParamKey[];
  customer_texts: { key: RuleParamKey; template_id: string; template_version: string; text_de: string }[];
  customer_texts_unavailable: { key: RuleParamKey; reason: "no_threshold_notice_template" }[];
  impact: {
    method: "stored_fact_threshold_crossings";
    baseline: "current_business_values";
    existing_requests_unchanged: true;
    limit: number;
    sampled_requests: number;
    keys: {
      key: RuleParamKey;
      status: "computed" | "partial" | "unavailable";
      reason: "not_a_threshold" | "no_comparable_stored_facts" | null;
      comparable_requests: number;
      unavailable_requests: number;
      inapplicable_requests: number;
      changed_requests: number | null;
      below_to_at_or_above: number | null;
      at_or_above_to_below: number | null;
    }[];
  };
}

/** A 400 `invalid_rule_parameters` issue, exactly as the server's registry parser reports it. */
export interface RuleParameterIssue {
  code:
    | "expected_object"
    | "expected_patch_object"
    | "unknown_key"
    | "missing_key"
    | "expected_integer"
    | "integer_out_of_bounds"
    | "expected_boolean"
    | "invariant_violation"
    | string;
  path: string[];
  invariantId?: string;
}

// ---- per-tenant feature/quota model (#239) --------------------------------

/** The closed capability catalog the owner gates per business. */
export type FeatureKey =
  | "ingest"
  | "ai_extraction"
  | "draft_compose"
  | "send"
  | "rule_params_edit"
  | "rule_studio"
  | "rule_proposal_ai"
  | "external_lookup"
  | "positions_offer"
  | "test_injection"
  | "single_agent_workflow"
  | "agent_rules_lab"
  | "rule_values_edit";

/** v1 quota dimensions (each homed on one feature). */
export type QuotaKey =
  | "ai_calls_per_month"
  | "sends_per_day"
  | "google_places_requests_per_month";

/** Tenant-wide (plan-level) quota dimensions — stored in plan_limits_json, not on a feature (#240). */
export type PlanQuotaKey = "operators_max";

/** Where an effective value came from (owner-panel provenance). */
export type FeatureSource = "override" | "default" | "catalog";

export interface OwnerTenantFeatureQuota {
  key: QuotaKey;
  limit: number;
  /** Current counted usage against this limit (#240). */
  usage: number;
  source: FeatureSource;
}

/** A tenant-wide plan quota for the owner panel — operators_max etc. (#240). */
export interface OwnerPlanQuota {
  key: PlanQuotaKey;
  limit: number;
  usage: number;
  source: FeatureSource;
}

/** Body for `POST /api/owner/tenants/:id/plan-limits`. */
export type SetPlanLimitsInput = Partial<Record<PlanQuotaKey, number>>;

/** One capability as `GET /api/owner/tenants/:id/features` returns it. */
export interface OwnerTenantFeatureView {
  key: FeatureKey;
  /** Effective enabled: env AND status AND toggle AND quota. */
  enabled: boolean;
  env_enabled: boolean;
  tenant_active: boolean;
  /** The toggle layer alone (what `enabled` would be if env+status allowed). */
  feature_enabled: boolean;
  quota_ok: boolean;
  limits: Partial<Record<QuotaKey, number>>;
  /** False ⇒ no env kill-switch above this feature. */
  has_env_flag: boolean;
  /** True ⇒ a per-tenant override row exists (not inherited). */
  overridden: boolean;
  source: FeatureSource;
  /** Value used when no override exists (owner default → catalog default). */
  inherited_enabled: boolean;
  quotas: OwnerTenantFeatureQuota[];
  version: number | null;
}

export interface OwnerTenantFeatures {
  tenant: { id: string; slug: string; displayName: string; status: TenantStatus | string };
  features: OwnerTenantFeatureView[];
  /** Tenant-wide plan quotas (operators_max) — usage + editable limit (#240). */
  plan_quotas: OwnerPlanQuota[];
  google_places_global_quota: GooglePlacesQuotaState;
}

/** One record in the owner defaults map or a per-tenant override body. */
export interface FeatureSettingValue {
  enabled?: boolean;
  limits?: Partial<Record<QuotaKey, number>>;
}

/** GET/POST /api/owner/feature-defaults — the owner-editable global defaults. */
export type FeatureDefaults = Partial<Record<FeatureKey, FeatureSettingValue>>;

/** GET/POST /api/owner/google-places-quota — installation-wide paid-call guard. */
export interface GooglePlacesQuotaState {
  usage: number;
  monthlyLimit: number;
  hardCeiling: number;
  windowStart: string;
  windowEnd: string;
}

/** POST /api/owner/tenants/:id/features/:key body (enabled required; the column is NOT NULL). */
export interface SetTenantFeatureInput {
  enabled: boolean;
  limits?: Partial<Record<QuotaKey, number>>;
}

// ---- per-business test injection (#677) -------------------------------------

/** One injectable scenario of the library. */
export interface TestMailScenarioOption {
  key: string;
  label_de: string;
  label_en: string;
}

/** An enquiry scenario, which additionally carries its group and its intended outcome (#673). */
export interface TestMailEnquiryOption extends TestMailScenarioOption {
  category: string;
  expectation_de: string;
  expectation_en: string;
}

/** A group the enquiry library is presented in. */
export interface TestMailEnquiryCategory {
  id: string;
  label_de: string;
  label_en: string;
}

/** GET /api/test-mail/catalog — the library plus the real mailbox an injection lands in. */
export interface TestMailCatalog {
  mailbox_address: string | null;
  scenarios: TestMailEnquiryOption[];
  categories: TestMailEnquiryCategory[];
}

/** POST /api/test-mail/inject | inject-random — names the scenario that arrived. */
export interface TestMailInjectResult {
  ok: true;
  request_id: string;
  message_id: string;
  scenario: TestMailEnquiryOption;
}

// ---- owner mailbox provisioning (#236) --------------------------------------

/** GET /api/owner/tenants/:id/mailboxes — masked: `*PasswordSet` booleans stand in for the
 *  stored credentials, which are write-only through the API and never returned. */
/** Owner catalog panel (#263): one tenant's catalog state. */
export interface TenantCatalogStats {
  total: number;
  active: number;
  packages: number;
  lastImportAt: string | null;
}

/** One catalog article as listed in the owner console (#382). Read-only. */
export interface CatalogArticleRow {
  id: string;
  bpId: string;
  nameDe: string;
  nameEn: string | null;
  gruppe: string | null;
  sparte: string | null;
  priceNet: number | null;
  unit: string | null;
  isPackage: boolean;
  isVegan: boolean;
  isVegetarian: boolean;
  active: boolean;
}

/** Paged, searchable article listing (#382). `total` counts the whole search result. */
export interface CatalogArticleList {
  items: CatalogArticleRow[];
  total: number;
  offset: number;
  limit: number;
}

/** Result of a per-tenant Artikel.csv / Artikelliste.xlsx import (#263, #310). */
export interface CatalogImportSummary {
  format: "csv" | "xlsx";
  sourceRows: number;
  parsed: number;
  imported: number;
  skipped: number;
  skippedByReason: {
    invalidRow: number;
    noPositivePrice: number;
    internalGroup: number;
  };
  packages: number;
  deactivated: number;
}

export interface TenantMailboxRow {
  id: string;
  tenantId: string;
  enabled: boolean;
  imapHost: string;
  imapPort: number;
  imapEmail: string;
  imapFolder: string;
  imapPasswordSet: boolean;
  imapTlsAllowHostnameMismatch: boolean;
  imapTlsExpectedServername: string | null;
  smtpHost: string | null;
  smtpPort: number;
  smtpUser: string | null;
  smtpPasswordSet: boolean;
  smtpFrom: string | null;
  smtpFromName: string | null;
  smtpSecure: boolean;
  smtpTlsAllowHostnameMismatch: boolean;
  /** #779: The platform hosts this mailbox. A business has at most one live, and cannot change it itself. */
  neoHosted: boolean;
  /** #1327: the business's own mail domain this address receives for, or null for every other mailbox. */
  mailDomainId: string | null;
  /** #1475: when this hosted address was retired. Its account is gone and the server refuses to enable it. */
  retiredAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Create/update body. Passwords are write-only: omit `imapPassword` on update to keep the
 *  stored one; `smtpPassword: null` clears it. Updates carry `version` (optimistic lock). */
export interface TenantMailboxInput {
  imapHost?: string;
  imapPort?: number;
  imapEmail?: string;
  imapFolder?: string;
  imapPassword?: string;
  imapTlsAllowHostnameMismatch?: boolean;
  imapTlsExpectedServername?: string | null;
  smtpHost?: string | null;
  smtpPort?: number;
  smtpUser?: string | null;
  smtpPassword?: string | null;
  smtpFrom?: string | null;
  smtpFromName?: string | null;
  smtpSecure?: boolean;
  smtpTlsAllowHostnameMismatch?: boolean;
  version?: number;
}

/** POST …/mailboxes/:id/check — strictly read-only connectivity self-check result. */
export interface TenantMailboxCheckResult {
  imap: { ok: boolean; uidValidity?: number; messageCount?: number; error?: string };
  smtp: { ok: boolean; error?: string } | null;
}

/** GET /api/owner/tenants/:id/readiness — per-tenant runtime diagnostics for the owner. */
export interface OwnerTenantReadiness {
  tenant: { id: string; slug: string; displayName: string; status: TenantStatus | string };
  ready: boolean;
  code_ruleset_version: string;
  rules_authority: "code_fixed";
  rule_parameters: RuleParameterRuntimeDiagnostic;
}

export type RuleParameterRuntimeDiagnostic =
  | {
      status: "healthy";
      feature_enabled: false;
      /** `tenant_set`: the owner-set values of this business (#1617). */
      source: "code_defaults" | "tenant_set";
      authority: "code_fixed";
      code_ruleset_version: string;
      parameter_set_version: number;
      effective_rules_hash: string;
      error_code: null;
    }
  | {
      status: "unhealthy";
      feature_enabled: false;
      /** `tenant_set`: the owner-set values of this business (#1617). */
      source: "code_defaults" | "tenant_set";
      authority: "code_fixed";
      code_ruleset_version: string;
      parameter_set_version: null;
      effective_rules_hash: null;
      error_code: string;
    };

// The live-update contract itself lives with the client that interprets it (#544/#549); the API
// seam only re-exports it so screens keep importing their types from one place.
export type {
  ConsoleChangedEvent,
  ConsoleDomain,
  ConsoleEventScope,
  ConsoleLiveEvent,
} from "@/live/live-updates";

export interface ConsoleEventHandlers {
  onEvent: (event: import("@/live/live-updates").ConsoleLiveEvent) => void;
  /** The stream dropped; the caller owns the reconnect policy. */
  onError: () => void;
}

export type RequestStatus =
  | "new" | "extracting" | "needs_information" | "ready_for_draft"
  | "draft_generating" | "draft_pending_review" | "sending" | "sent"
  | "needs_human_review" | "closed" | "failed";

export type RequestLifecycleStatus =
  | "new" | "in_progress" | "new_message" | "answered" | "done";

export type ReviewState = "pending" | "edited" | "approved" | "rejected";

export type SendState = "IDLE" | "SENDING" | "SENT" | "UNKNOWN" | "FAILED_BEFORE_SUBMISSION";

/** GET /api/requests — worklist row. */
export interface ThreadTargetPage {
  items: { id: string; reference: string | null; subject: string | null }[];
  next_offset: number | null;
}

export interface RequestListItem {
  incoming_work?: IncomingWork | null;
  id: string;
  conversation_id?: string;
  request_no?: number | null;
  reference?: string | null;
  subject: string | null;
  from: string | null;
  status: RequestStatus | string;
  lifecycle_status: RequestLifecycleStatus;
  lifecycle_changed_at?: string | null;
  assignees: Assignee[];
  latest_message_received_at?: string | null;
  /** Monotonic per-request cursor for exact inbound arrival deltas (#624). */
  inbound_message_count?: number;
  /** One-based cursor position that was the tenant-new contact; null for known customers. */
  new_customer_inbound_message_index?: number | null;
  updated_at?: string | null;
  response_type?: string | null;
  requires_human_review?: boolean;
  draft_review_state?: ReviewState | null;
  draft_is_current?: boolean | null;
  draft_stale_reason?: string | null;
  workflow?: { result_id: string | null; current: false | null; reason: string; state: string;
    processing: import("../../../../core/src/workflow/workflow-processing").WorkflowProcessing } | null;
  has_unhandled_reply?: boolean;
  /** #677: injected test mail rather than a customer's — marked so nobody works it as real. */
  is_test?: boolean;
  analysis?: AnalysisStateView | null;
}

/**
 * #815: the event context the server resolved for the row's target request, from accepted facts
 * only. A null is the honest answer for "the system does not know", never a placeholder.
 */
export interface EventSummary {
  occasion: string | null;
  /** Calendar date as YYYY-MM-DD, or null when the accepted value is not one date. */
  event_start_date: string | null;
  venue_city: string | null;
}

/** One server-owned row in GET /api/conversations. */
export const CONVERSATION_ATTENTION_REASONS = [
  "new_reply",
  "human_review",
  "decision_recompute",
  "stale_draft",
  "draft_rejected",
  "draft_review",
  "draft_send",
  "new_request",
] as const;
export type ConversationAttentionReason = (typeof CONVERSATION_ATTENTION_REASONS)[number];

export interface ConversationListItem {
  incoming_work?: IncomingWork | null;
  contact_form_source?: "gravity_forms" | null;
  source_address?: string | null;
  confirmed_customer_email?: string | null;
  /** Stable row identity: request ID in request mode, conversation ID otherwise. */
  id: string;
  conversation_id?: string | null;
  lifecycle_status: RequestLifecycleStatus;
  /** Closed server-owned reason for the target request; the browser only translates it. */
  attention_reason: ConversationAttentionReason | null;
  highlighted: boolean;
  /** Team-wide first-opening facts; absence is unknown during a rolling update. */
  unopened?: boolean;
  opening_state?: "unopened" | "opened" | "legacy_unknown";
  review_needed: boolean;
  active_request_count: number;
  target_request_id: string;
  /** Version displayed with the row; missing during a rolling update disables selection. */
  target_request_version?: number;
  target_request_no: number | null;
  target_reference: string | null;
  subject: string | null;
  from: string | null;
  last_message_snippet: string | null;
  last_message_direction: string | null;
  latest_activity_at: string;
  event_summary: EventSummary;
  /** Accepted facts of this row's target request, never conversation-wide profile fields. */
  customer_name: string | null;
  /** Accepted optional ordering-party type for this request, absent on older API responses. */
  customer_type?: "private" | "business" | null;
  guest_count: GuestCountValue | null;
  assignees: Assignee[];
  has_unhandled_reply: boolean;
  /** #677: at least one request in this conversation is injected test mail. */
  is_test?: boolean;
  /** #1787: set on trash rows only — when it was set aside and when it is deleted for good. */
  trashed_at?: string | null;
  purge_at?: string | null;
  analysis?: AnalysisStateView | null;
}

/**
 * #813: which half of the worklist a page came from. Every conversation is in exactly one, decided
 * by the server from the request lifecycle — the archive is not a filter the console applies. The
 * trash (#1787) holds the requests an operator set aside; it always lists requests.
 */
export type ConversationScope = "active" | "archived" | "trash";

/**
 * #814: the complete sort catalog. The console names one of these; the order it stands for is
 * written in the server's own SQL, so no column name or direction ever travels over the wire.
 */
export type ConversationSort = "activity_desc" | "activity_asc";

export interface ConversationListPage {
  items: ConversationListItem[];
  group_by?: "conversation" | "request";
  total: number;
  scope: ConversationScope;
  sort: ConversationSort;
  limit: number;
  offset: number;
  next_offset: number | null;
  counts: {
    incoming_work?: IncomingWorkCounts;
    total: number;
    attention: number;
    unopened?: number;
    unassigned: number;
    by_status: Record<RequestLifecycleStatus, number>;
  };
}

export interface ConversationListOptions {
  queue?: "all" | "new" | "review" | "triage";
  group_by?: "conversation" | "request";
  q?: string;
  status?: RequestLifecycleStatus | "";
  assignment?: "all" | "review" | "unassigned" | "review_unassigned";
  /** Inclusive period in the tenant's calendar, each end optional, as YYYY-MM-DD. */
  date_from?: string;
  date_to?: string;
  sort?: ConversationSort;
  scope?: ConversationScope;
  limit?: number;
  offset?: number;
}

/** GET /api/requests/:id/previous-requests — older requests in the same conversation. */
export interface PreviousRequestSummary {
  id: string;
  request_no: number;
  reference: string | null;
  status: RequestStatus | string;
  lifecycle_status: RequestLifecycleStatus;
  lifecycle_changed_at: string;
  subject: string | null;
  latest_message_received_at: string | null;
  done_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PreviousRequestsView {
  active_request_id: string;
  next_cursor: string | null;
  previous_requests: PreviousRequestSummary[];
}

/** GET /api/requests/:id/customer-messages — every email of the request's customer (#1405). */
export interface CustomerMailItem {
  id: string;
  request_id: string;
  request_no: number | null;
  reference: string | null;
  /** The message belongs to the request on screen. */
  current: boolean;
  direction: "inbound" | "outbound" | string;
  from_addr: string | null;
  to_addrs: string[];
  received_at: string | null;
  subject: string | null;
  /** Whitespace-collapsed plain text, bounded server-side. */
  preview: string;
  forwarded_by_addr: string | null;
}

export interface CustomerMailView {
  request_id: string;
  /** More messages exist than the newest ones returned. */
  truncated: boolean;
  messages: CustomerMailItem[];
}

export interface Fact {
  authority?: { locked: boolean; confirmed: boolean };
  fieldPath: string;
  version?: number;
  value: unknown;
  status: string;
  isLocked: boolean;
  evidenceText?: string | null;
  confidence?: number | null;
  /** Real API field name (facts.source_type). */
  sourceType?: string | null;
  source_message_id?: string | null;
  manually_overridden_by?: string | null;
  override_reason?: string | null;
  overrideReason?: string | null;
  updatedAt?: string | null;
}

export interface DecisionStateView {
  response_type?: string | null;
  request_status?: string | null;
  redirect_to_online_shop?: boolean;
  quote_eligible?: boolean | null;
  required_questions?: Array<{
    id?: string;
    meaning?: string;
    text?: string;
    field?: string;
    fields?: string[];
  }>;
  suppressed_questions?: string[];
  required_notices?: string[];
  missing_fields?: string[];
  allowed_actions?: Record<string, boolean>;
  forbidden_actions?: string[];
  forbidden_topics?: string[];
  commercial_outcome?: {
    cutoff?: HamburgCutoffView;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

export interface HamburgCutoffChangeView {
  field_path: string;
  value: unknown;
  source_message_id: string | null;
}

export interface HamburgCutoffView {
  outcome: "ON_TIME" | "LATE_INITIAL_SUBMISSION" | "LATE_MATERIAL_CHANGE" | "CHANGE_FORBIDDEN" | "UNRESOLVED";
  reason_code:
    | "WITHIN_INITIAL_ORDER_DEADLINE"
    | "INITIAL_ORDER_DEADLINE_PASSED"
    | "WITHIN_MATERIAL_CHANGE_DEADLINE"
    | "MATERIAL_CHANGE_DEADLINE_PASSED"
    | "ONLINE_SHOP_ORDER_CHANGE_FORBIDDEN"
    | "TIMESTAMP_PROVENANCE_UNRESOLVED"
    | "EVENT_DATE_INVALID"
    | "CALENDAR_OUT_OF_RANGE";
  kind: "INITIAL_SUBMISSION" | "MATERIAL_CHANGE" | "ONLINE_SHOP_CHANGE";
  calendar_version: "hamburg-business-calendar-2026-2030-v1";
  time_zone: "Europe/Berlin";
  event_date: string | null;
  authoritative_at: string | null;
  cutoff_at: string | null;
  cutoff_inclusive: boolean;
  order_submitted_at: string | null;
  applied_changes: HamburgCutoffChangeView[];
  rejected_changes: HamburgCutoffChangeView[];
}

export type DecisionIntegrityIssueCode =
  | "decision_state_invalid"
  | "outer_matched_rule_ids_invalid"
  | "outer_review_reasons_invalid"
  | "duplicate_matched_rule_id"
  | "matched_rule_ids_mismatch"
  | "ruleset_version_mismatch"
  | "requires_human_review_mismatch"
  | "review_reasons_mismatch"
  | "canonical_evidence_invalid"
  | "parameter_set_mismatch"
  | "input_facts_hash_mismatch";

export type DecisionActionBlocker =
  | "decision_integrity_failure"
  | "request_revision_stale"
  | "request_input_stale"
  | "code_ruleset_stale"
  | "code_ruleset_not_evaluable"
  | "parameter_set_stale"
  | "rule_params_hash_stale"
  | "effective_rules_hash_stale"
  | "policy_currentness_not_evaluable"
  | "policy_version_stale"
  | "post_cutover_read_only"
  | "invalid_cutover_marker";

export interface DecisionActionSafetyView {
  integrity_status: "valid" | "invalid";
  integrity_issues: Array<{ code: DecisionIntegrityIssueCode; [key: string]: unknown }>;
  code_currentness: "current" | "code_ruleset_stale" | "not_evaluable";
  active_code_ruleset_version: string;
  policy_currentness: "not_applicable" | "not_evaluable" | "current" | "policy_stale";
  request_policy_version: number | null;
  request_policy_payload_hash: string | null;
  writer_posture: "legacy_write_enabled" | "canonical_write_enabled" | "post_cutover_read_only" | "invalid_marker_read_only";
  action_blockers: DecisionActionBlocker[];
}

export type IdentityResolutionStatus =
  | "legacy_resolved"
  | "canonical"
  | "unchanged"
  | "invalid_era"
  | "unresolved_id"
  | "unresolved_ruleset";

export type IdentityEvidenceStatus = "archived_exact" | "persisted_exact" | "unavailable";

export type RuleIdentityCutoverState = "pre_cutover" | "canonical_cutover" | "invalid";

export interface DecisionRuleDetail {
  id: string;
  recorded_id: string;
  canonical_id: string | null;
  resolution_status: IdentityResolutionStatus;
  evidence_status: IdentityEvidenceStatus;
  display_key: string | null;
  version: string | null;
  priority: number | null;
  scope: string | null;
  summary: string;
  known: boolean;
}

export interface DecisionNoticeIdentityDetail {
  recorded_id: string;
  canonical_id: string | null;
  resolution_status: IdentityResolutionStatus;
  evidence_status: IdentityEvidenceStatus;
  display_key: string | null;
  text_de: string | null;
}

/** #1684: Core's explanation of the persisted decision. The workspace renders it and decides nothing. */
export interface DecisionExplanationView {
  ruleset_version: string;
  response_type: string;
  guest_threshold: {
    relation: "at_or_above" | "below" | "count_unknown" | "not_recorded";
    /** Only a value proven by the decision's recorded rule-values hash. */
    min_written_offer_guests: number | null;
  };
  rule_values: { source: "business"; version: number } | { source: "defaults" } | { source: "unverified" };
  commercial: {
    outcome: string;
    reason_code: string;
    threshold_net_eur: number | null;
    food_subtotal_net_eur: number | null;
  } | null;
  /** rule_id is null on decisions recorded before #1684. */
  questions: Array<{ id: string; meaning: string | null; rule_id: string | null }>;
  /** Core owns ordering and the heading threshold; absent on older servers. */
  question_groups?: Array<{
    topic: "event" | "food" | "logistics" | "other" | null;
    questions: Array<{ id: string; meaning: string | null; rule_id: string | null }>;
  }>;
  review_reasons: Array<{ code: string; text: string }>;
}

export interface DecisionView {
  id?: string;
  request_revision?: number;
  state: DecisionStateView | null;
  persisted_state?: unknown;
  action_safety?: DecisionActionSafetyView;
  operator_notices?: Array<{ text: string }>;
  ruleset_version?: string;
  policy_version_id?: string | null;
  policy_version?: number | null;
  policy_payload_hash?: string | null;
  parameter_set_id?: string | null;
  parameter_set_version?: number | null;
  rule_params_hash?: string | null;
  effective_rules_hash?: string | null;
  matched_rule_ids?: unknown;
  matched_rule_details?: DecisionRuleDetail[];
  rule_identity_cutover_state?: RuleIdentityCutoverState;
  notice_identity_details?: DecisionNoticeIdentityDetail[];
  requires_human_review?: boolean;
  review_reasons?: unknown;
  /** Absent from servers before #1684; the panel validates it before rendering. */
  explanation?: unknown;
}

/** draft.response_plan — the deterministic plan the composer recorded; retained on the
 *  contract and in draft history, no longer shown in the operator workspace (#592). */
export interface ResponsePlanView {
  response_type?: string;
  language?: string;
  /** #1200: which round the draft answers; older drafts carry no value (initial). */
  conversation_turn?: "initial" | "reply";
  customer_name?: string | null;
  facts_allowed_to_mention?: Record<string, unknown>;
  required_questions?: Array<{ id: string; text: string }>;
  required_notices?: Array<{ id: string; text: string }>;
  forbidden_topics?: string[];
  signature_template_id?: string;
}

export interface MessageView {
  id?: string;
  direction: "inbound" | "outbound" | string;
  fromAddr?: string | null;
  toAddrs?: string[] | null;
  receivedAt?: string | null;
  subject?: string | null;
  rfcMessageId?: string | null;
  inReplyTo?: string | null;
  references?: string[] | null;
  normalizedText?: string | null;
  /**
   * #594: the sender's own formatting, sanitized server-side on every read. Safe to render as
   * markup; null when the message carries nothing beyond its plain text.
   */
  bodyHtml?: string | null;
  /** The sender embedded images, which are never rendered — the reader is told rather than
   *  left with a silent gap. */
  bodyImagesRemoved?: boolean;
  /** Set when the sender DID send markup that is not on screen, so nothing is dropped in
   *  silence: too large to ship, or nothing renderable survived sanitizing. */
  bodyWithheld?: "too_large" | "nothing_renderable" | null;
  /** #594: what the customer attached, refused parts included. */
  attachments?: AttachmentView[];
  /**
   * #1385: the trusted address this enquiry was forwarded from, when it was. Present only when
   * the sender is on this business's forwarder list, which is why an ordinary message carries
   * neither this nor the status below.
   */
  forwardedByAddr?: string | null;
  /**
   * `resolved` — the fields above describe the customer's own message, and `forwardedByAddr` is
   * the colleague who passed it on. Anything else is why the forward could not be read, in which
   * case this message is the colleague's and the reply will go to them.
   */
  forwardedOriginStatus?: ForwardedOriginStatus | null;
}

/** Why a forward from a trusted address did or did not produce a customer (#1385). */
export type ForwardedOriginStatus =
  | "resolved"
  | "unauthenticated"
  | "no_forwarded_message"
  | "ambiguous_forwarded_message"
  | "invalid_forwarded_sender"
  | "self_forwarded_sender";

/** Why an attachment cannot be opened. Decided at ingest and recorded, never re-derived. */
export type AttachmentRejection =
  | "too_large"
  | "message_budget_exhausted"
  | "unsupported_type"
  | "empty";

/**
 * One of the customer's attachments (#594). `filename` and `contentType` are the server's
 * sanitized values, not the sender's headers. A refused part still appears — an operator has to
 * know a floor plan arrived even when it is not downloadable.
 */
export interface AttachmentView {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  disposition: "attachment" | "inline";
  available: boolean;
  rejection: AttachmentRejection | string | null;
}

export interface ProposalScopeView {
  components: Array<{
    component: "bbq" | "menu" | "buffet" | "fingerfood" | "trade_fair" | "live_cooking" | "drinks" | "coffee" | "staffing" | "equipment";
    status: "included" | "manual" | "unavailable" | "external";
    reason: "selected" | "secondary_type" | "manual_food" | "selection_required" | "specific_drinks" | "catalog_mapping" | "commercial_restriction" | "external_supply" | "configuration_required";
    position_ids: string[];
  }>;
}

export interface DraftOfferSnapshotView {
  scope?: ProposalScopeView;
  eligible?: boolean;
  calculatedPartialTotalNet?: number | null;
  occasion: PositionOccasion | null;
  quantityRulesetVersion: string | null;
  selectionStatus: "legacy" | "occasion_applied" | "occasion_fallback";
  staffingFormat?: PositionStaffingFormat | null;
  durationHours?: number | null;
  includeStaffing?: boolean;
  staffingPolicyVersion?: string | null;
  staffingStatus?: "legacy" | "removed" | "applied" | "not_applied";
  estimatedTotalNet: number | null;
  positions: Array<{
    bpId: string;
    name: string;
    quantity: number;
    unit: string | null;
    priceNet: number | null;
    role: string;
  }>;
}

export interface OutboundArtifactPreview {
  content_hash: string;
  from: string;
  from_name: string | null;
  to: string;
  subject: string;
}

export interface DraftView {
  recipient?: { default_address: string | null; override_address: string | null; effective_address: string | null } | null;
  outbound_artifact?: OutboundArtifactPreview | null;
  outbound_artifact_error?: string | null;
  id: string;
  decision_id?: string | null;
  review_state: ReviewState | string;
  /** Proven human rejection, independent of automatic invalidation/currentness. */
  human_rejected?: boolean;
  revision: number;
  /** Optimistic-lock version; changes whenever the reviewed draft content or state changes. */
  version: number;
  request_revision?: number;
  subject?: string | null;
  body_text?: string | null;
  body_html?: string | null;
  /** Present on new drafts; only this fragment is editable. body_* is the complete reviewed mail. */
  reply_body_html?: string;
  reply_body_text?: string;
  /** Server-owned closing; visible during review, excluded from the reply editor. */
  closing?: { text: string; html: string };
  signature?: { html: string; logo: EmailSignatureLogo | null };
  response_plan?: ResponsePlanView | null;
  offer_snapshot?: DraftOfferSnapshotView | null;
  is_current_decision?: boolean | null;
  stale_reason?: string | null;
  attachments?: OutboundDraftAttachmentView[];
  attachments_mutable?: boolean;
}

export interface OutboundDraftAttachmentView {
  id: string;
  filename: string;
  content_type: "application/pdf" | "image/png" | "image/jpeg" | string;
  byte_size: number;
  available: boolean;
  download_url: string;
}

export interface DraftAttachmentMutationResult {
  ok: true;
  draftId: string;
  version: number;
  manifestHash: string;
}

export interface DraftAttachmentCancellationResult {
  cancelled: boolean;
  committed: boolean;
}

/** POST /api/drafts/:id/first-view — whether this operator may reveal this draft (#804). */
export interface DraftFirstViewClaim {
  /** True only for the caller that recorded the view; everyone else renders it at once. */
  claimed: boolean;
}

export interface DeliveryView {
  operatorCommandKey?: string | null;
  operatorExpectedVersion?: number | null;
  operatorRequestRevision?: number | null;
  approvedArtifactHash?: string | null;
  recipientAddr?: string | null;
  state?: SendState | string;
  deliveryUncertain?: boolean;
  attemptId?: string | null;
  draft_id?: string | null;
  attemptNumber?: number | null;
  errorCode?: string | null;
  startedAt?: string | null;
  smtpMessageId?: string | null;
  providerMessageId?: string | null;
  finishedAt?: string | null;
}

/**
 * What the relay reported AFTER it accepted the message (#711). Separate from `DeliveryView`
 * on purpose: the SMTP handoff above really did succeed, and a bounce is a later, different
 * fact. `null` means nothing went wrong, which is the ordinary case.
 */
export interface DeliveryFailureView {
  kind: "hard_bounce" | "soft_bounce" | "complaint" | string;
  occurred_at: string | null;
  recipient: string | null;
  /** The remote server's own words. Free text from a third party — display only. */
  diagnostic: string | null;
  soft_bounce_count: number;
  soft_bounce_threshold: number;
}

/**
 * GET /api/owner/neo-mailboxes/offer — whether this installation hosts mailboxes at all (#710).
 * An installation without a mail server has nothing to offer, and the form hides the choice
 * rather than presenting an option that always fails.
 */
export interface NeoMailboxOffer {
  offered: boolean;
  domain: string | null;
}

/**
 * GET /api/owner/neo-mailboxes/availability — the server's verdict on one name (#710).
 *
 * The rules live in the backend and only there; the browser asks rather than deciding, so the
 * two can never drift into promising an address that cannot be issued.
 */
export type NeoMailboxAvailability =
  | { status: "available"; emailAddress: string; localPart: string }
  | { status: "taken"; emailAddress: string }
  | { status: "invalid"; reason: string; message: string }
  | { status: "unconfigured" };

/** POST /api/owner/tenants/:id/mailboxes/neo-hosted — no credential is ever returned. */
export interface NeoMailboxProvisionResult {
  mailboxId: string;
  emailAddress: string;
  enabled: boolean;
}

/**
 * POST /api/owner/tenants/:id/mailboxes/neo-hosted/change (#779) — the owner-only correction.
 * `previous` is the address that has just been closed, so the confirmation can name it.
 */
export interface NeoMailboxChangeResult extends NeoMailboxProvisionResult {
  previous: string;
}

/**
 * POST /api/owner/tenants/:id/mail-domains/:mailDomainId/mailbox/change (#1447) — the owner-only
 * change of the address a business's own connected domain receives at. `previous` has just closed.
 */
export interface MailDomainAddressChangeResult {
  mailboxId: string;
  emailAddress: string;
  previous: string;
}

/**
 * An invitation to finish setting a business up (#780). Never carries the token: it exists in
 * plaintext exactly once, in the response to issuing it.
 */
export interface TenantInvitation {
  id: string;
  tenantId: string;
  invitedEmail: string | null;
  invitedName: string | null;
  expiresAt: string;
  createdAt: string;
  revokedAt: string | null;
  acceptedAt: string | null;
  state: "outstanding" | "expired" | "revoked" | "accepted";
}

/** GET /api/owner/platform-mailboxes — what an account on the platform's mail domain is (#1214). */
export type PlatformMailboxCategory = "service" | "tenant" | "system" | "unmanaged";

/**
 * Which of the platform's own mail domains an account is on (#1899): `platform` is the mail
 * domain businesses share, `work` the company's own addresses for people.
 */
export type PlatformMailDomainKind = "platform" | "work";

/**
 * GET /api/owner/platform-mailboxes/domains — where a mailbox can be made, and how a person
 * connects to a work mailbox (#1899). Null where this installation has none.
 */
export interface PlatformMailDomains {
  platform: string | null;
  work: string | null;
  webmail: string | null;
  /** The mail server's public hostname: IMAP on 993 and submission on 465, both over TLS. */
  mailHost: string | null;
}

/** One account on one of the platform's mail domains, as the owner console sees it (#1214, #1899). */
export interface PlatformMailboxView {
  localPart: string;
  emailAddress: string;
  domain: PlatformMailDomainKind;
  description: string;
  category: PlatformMailboxCategory;
  /** Set for `service` rows only; the mail server keeps no creation time we can read. */
  createdAt: string | null;
  createdBy: string | null;
}

/** POST /api/owner/platform-mailboxes and .../reset-password — the one time the password is readable. */
export interface PlatformMailboxSecret {
  localPart: string;
  emailAddress: string;
  domain: PlatformMailDomainKind;
  password: string;
}

/**
 * POST .../:localPart/webmail-session — a session in a service mailbox (#1235, #1265).
 *
 * The console still never holds a mailbox password. The server rotates the mailbox's credential
 * to one the mail server itself expires and scopes to the webmail's network, and this carries a
 * single-use ticket the webmail exchanges for it. The credential is not here and never reaches
 * this application.
 *
 * The ticket is POSTed to `url`, not appended to it: a query string lands in the webmail's access
 * log, and a spent ticket sitting in a log is a secret kept for no reason.
 */
export interface PlatformMailboxWebmailSession {
  localPart: string;
  emailAddress: string;
  url: string;
  /** Single-use, valid for about a minute. Use it immediately or discard it. */
  ticket: string;
  /** When the underlying session credential dies, ISO-8601. Part of the response; nothing renders it. */
  expiresAt: string;
}

/**
 * GET /api/mail-domains — a business's own mail domains, as the console shows them (#1328).
 *
 * The state is the server's word, not a status code the browser interprets. Four states because
 * four different things are true, and each one belongs to a different person: waiting is theirs,
 * a failed lookup is ours, finishing is ours, and receiving is nobody's. Collapsing any two of
 * them puts somebody on the phone. Once a domain receives, its proof is no longer re-checked
 * (#1424), so there is no fifth state for a proof that has since gone.
 */
export type MailDomainState =
  | "awaitingRecord"
  | "checkUnavailable"
  | "finishing"
  | "receiving";

/** What the last look at DNS saw, verbatim, so the screen quotes it rather than infers it. */
export type MailDomainCheckOutcome = "found" | "missing" | "unreachable";

/** One line of a DNS panel, exactly as the business has to type it. */
export interface MailDomainRecord {
  kind: "mx" | "txt" | "dkim" | "returnPath";
  /** Fully qualified, with the trailing dot — panels that append the zone origin need it. */
  name: string;
  type: "MX" | "TXT" | "CNAME";
  /** MX only; null for a type that has no preference field. */
  priority: number | null;
  value: string;
}

/**
 * Whether replies also leave as this address, and if not, which record is missing (#1329).
 *
 * Receiving and sending are separate facts about one domain: a business receives for months
 * before anybody asks to send as it, and a domain whose DKIM record a registrar dropped is still
 * receiving perfectly. The two verdicts arrive individually as well as through the state so the
 * screen can point at the one record that is wrong instead of sending somebody to re-check both.
 */
export type MailDomainSendingState =
  /** This installation cannot send from customer domains at all; nothing is offered. */
  | "unavailable"
  /** Receiving works; nobody has asked to send as this domain. */
  | "notRequested"
  /** Registered with the relay; neither record accepted yet. Normal, and theirs to wait out. */
  | "awaitingRecords"
  | "dkimMissing"
  | "returnPathMissing"
  /** Replies leave as the business's own address. */
  | "sending"
  /** Both were accepted, but the last verdict is too old to keep acting on. */
  | "stale";

export interface MailDomainSending {
  state: MailDomainSendingState;
  dkimVerified: boolean;
  returnPathVerified: boolean;
  /** When sending was asked for; the screen words the relay's wait by how long it has been. */
  requestedAt: string | null;
  checkedAt: string | null;
}

export interface MailDomainView {
  id: string;
  domain: string;
  state: MailDomainState;
  lastCheckOutcome: MailDomainCheckOutcome | null;
  lastCheckedAt: string | null;
  verifiedAt: string | null;
  /** Where enquiries arrive once the domain is receiving. Shown before then too. */
  emailAddress: string;
  records: MailDomainRecord[];
  sending: MailDomainSending;
}

/**
 * GET /api/trusted-forwarders — one address a business allows to forward customer mail in
 * (#1385). Admin-only, tenant-scoped; `address` is the normalized value the server compares
 * against, `originalAddress` is what was typed.
 */
export interface TrustedForwarderView {
  id: string;
  address: string;
  originalAddress: string;
  label: string | null;
  createdAt: string;
}

/** POST /api/mail-domains/:id/check — the claim as it now stands, and why nothing was proven. */
export interface MailDomainCheckResult {
  domain: MailDomainView;
  /**
   * The name that was queried, so the screen names the record rather than only the failure. Null
   * when DNS was not asked: a check on a receiving domain only re-reads sending (#1424).
   */
  recordName: string | null;
  detail: string | null;
}

/**
 * GET /api/setup-state — how far a business is from being finished (#1286).
 *
 * One read behind both the Getting started page and the reminder strip (#1287), so the two can
 * never disagree in front of the same person. Every field is a server fact; the browser renders
 * them and decides nothing — including what counts as "has colleagues", which is why that arrives
 * as a boolean rather than as a count for the page to threshold.
 */
export interface SetupState {
  signatureSet: boolean;
  hasColleagues: boolean;
  /** False means only the platform side can fix it, so it is stated and never offered as a task. */
  mailReceivable: boolean;
}

/** POST .../invitations — the one and only time the token is readable. */
export interface IssuedInvitation {
  id: string;
  token: string;
  expiresAt: string;
  /** The address the link was issued for, or null when the business has no owner yet (#1305). */
  invitedEmail: string | null;
  /**
   * Whether the link was emailed (#1305). `null` when no address was given, which is not a
   * failure — a business is often prepared before anybody knows who will run it, and then the
   * owner passes the link on exactly as before. The panel keeps those two answers apart.
   */
  emailSent: boolean | null;
}

/** POST /api/onboarding/preview — what the person holding the link is shown. */
export interface InvitationPreview {
  displayName: string;
  slug: string;
  invitedEmail: string | null;
  invitedName: string | null;
  expiresAt: string;
  /** The domain addresses are hosted on, or null where this installation hosts none. */
  mailboxDomain: string | null;
}

/**
 * GET /api/staff-invitations — a colleague invitation as the business that issued it sees it
 * (#1298). No token: the server keeps only a hash, so there is nothing to show a second time.
 */
export interface StaffInvitation {
  id: string;
  invitedEmail: string;
  invitedName: string | null;
  role: string;
  expiresAt: string;
  createdAt: string;
  revokedAt: string | null;
  acceptedAt: string | null;
  state: "outstanding" | "expired" | "revoked" | "accepted";
}

/** POST /api/staff-invitations — the one response the plaintext token ever appears in. */
export interface IssuedStaffInvitation {
  id: string;
  token: string;
  expiresAt: string;
  invitedEmail: string;
  role: string;
  /**
   * Whether the colleague was emailed their link (#1304). False is not an error — the invitation
   * exists either way — but it changes what the admin has to do next, so the panel says which
   * happened rather than letting them assume the message went.
   */
  emailSent: boolean;
}

/**
 * POST /api/onboarding/staff/preview — what the invited colleague is shown.
 *
 * The address and the role are facts of the invitation, not fields: the accept screen renders them
 * and cannot change them.
 */
export interface StaffInvitationPreview {
  businessName: string;
  invitedEmail: string;
  invitedName: string | null;
  role: string;
  expiresAt: string;
}

/** POST /api/onboarding/accept — the session arrives as a cookie, never in the body. */
export interface AcceptInvitationInput {
  token: string;
  displayName: string;
  slug: string;
  name?: string;
  email: string;
  password: string;
  /** The address they picked. Chosen once, here, and never editable by them afterwards (#779). */
  localPart?: string;
}

/**
 * GET /api/neo-mailbox (#779) — what address this business is reachable at. The only mailbox
 * fact the tenant plane exposes, and there is no write beside it: the address is chosen once
 * during registration and corrected only by an owner.
 */
export interface TenantNeoMailboxView {
  emailAddress: string | null;
  /**
   * #1229: true when the address lives on our mail server. It travels with the address because
   * the two are not interchangeable to a reader — only a hosted address is fixed at registration
   * and impossible to sign into with a mail client.
   */
  hosted: boolean;
  /**
   * #1432: every address the business receives at, and where replies to mail arriving there leave
   * from. The server computes it with the send path's own selection; the console only shows it.
   * Absent from a server older than #1432.
   */
  receiving?: ReceivingAddressRoute[];
}

/** What kind of address a business receives at (#1432). */
export type ReceivingAddressKind =
  /** Issued by us on our own mail domain — the working address. */
  | "platform"
  /** The one address on a domain the business proved and we host; `anfragen@` unless it chose a name (#1446). */
  | "ownDomain"
  /** A mailbox the business brought, read over IMAP. */
  | "ownMailbox";

export interface ReceivingAddressRoute {
  address: string;
  kind: ReceivingAddressKind;
  /** Null when nothing of this business's can send, which is also when a send would be refused. */
  repliesFrom: string | null;
}

/** GET /api/delivery/uncertain — every unresolved UNKNOWN attempt. */
export interface UncertainDeliveryAttempt {
  attemptId: string;
  state: "UNKNOWN";
  attemptNumber: number;
  errorCode: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  smtpMessageId: string | null;
  providerMessageId: string | null;
  draftId: string;
  requestId: string;
  requestNo: number | null;
  reference: string | null;
  subject: string | null;
  recipient: string | null;
  isCurrentDraft: boolean;
}

/** POST /api/send-attempts/:id/resolve — audited operator decision. */
export interface DeliveryResolutionResult {
  ok: true;
  attemptId: string;
  resolved: "delivered" | "not_delivered";
  requestStatusUpdated: boolean;
}

/**
 * One principal on a request (#803): a person or a whole team, with no primary among them.
 *
 * A team carries `memberCount` because "Spätschicht" alone does not tell an operator how many
 * people that is, and `archived` because a team archived after it was assigned keeps working the
 * request — the screen has to be able to say so rather than showing it as ordinary.
 */
/** What the console names when it adds or removes somebody: a kind and an id, nothing more. */
export interface AssigneeRef {
  kind: "user" | "team";
  id: string;
}

/** Every assignment write answers with the whole set, so the screen never has to guess it. */
export interface AssigneeSetResult {
  ok: true;
  assignees: Assignee[];
}

export type Assignee =
  | { kind: "user"; id: string; name?: string | null; email: string }
  | { kind: "team"; id: string; name: string; archived: boolean; memberCount: number };

export interface RequestActivityPage {
  events: Array<{
    id: string;
    action: string;
    actor: { kind: "human" | "sol" | "system" | "unknown"; name: string | null };
    created_at: string;
  }>;
  next_cursor: string | null;
}

export interface AuditEvent {
  id: string;
  event_type: string;
  payload?: unknown;
  ruleset_version?: string | null;
  actor_email?: string | null;
  created_at?: string | null;
}

export type ReviewGuidanceSeverity = "danger" | "warning" | "info";
export type ReviewGuidanceBlockerCode =
  | "DELIVERY_UNCERTAIN"
  | "SENDING_IN_PROGRESS"
  | "SUBMISSION_PENDING"
  | "REQUEST_NOT_SENDABLE"
  | "DECISION_SAFETY_UNAVAILABLE"
  | "DECISION_SAFETY_BLOCKED"
  | "MISSING_DECISION"
  | "EXTRACTION_FAILED"
  | "EXTRACTION_IN_PROGRESS"
  | "HUMAN_REVIEW_REQUIRED"
  | "DRAFT_GENERATION_IN_PROGRESS"
  | "DRAFT_GENERATION_DISABLED"
  | "MISSING_DRAFT"
  | "STALE_DRAFT"
  | "DRAFT_REJECTED"
  | "DRAFT_APPROVAL_REQUIRED"
  | "ATTACHMENT_STORAGE_UNAVAILABLE"
  | "SENDING_DISABLED";
export type ReviewGuidanceSummaryCode =
  | ReviewGuidanceBlockerCode
  | "READY_TO_SEND"
  | "SUBMISSION_FAILED"
  | "SENT"
  | "CLOSED_REQUEST";
export type ReviewGuidanceNextActionCode =
  | "NONE"
  | "WAIT"
  | "CONTACT_ADMIN"
  | "RETRY_ANALYSIS"
  | "RECOMPUTE_DECISION"
  | "REVIEW_EVIDENCE"
  | "GENERATE_DRAFT"
  | "REGENERATE_DRAFT"
  | "REVIEW_DRAFT"
  | "ENABLE_SENDING"
  | "SEND_DRAFT"
  | "CONTINUE_SUBMISSION"
  | "RECONCILE_DELIVERY";
export interface ReviewGuidanceBlockerView {
  code: ReviewGuidanceBlockerCode;
  severity: ReviewGuidanceSeverity;
}
export interface ReviewGuidanceView {
  schema_version: 1;
  state: "blocked" | "in_progress" | "ready" | "complete";
  summary_code: ReviewGuidanceSummaryCode;
  primary_blocker: ReviewGuidanceBlockerView | null;
  additional_blockers: ReviewGuidanceBlockerView[];
  additional_blocker_count: number;
  next_action: { code: ReviewGuidanceNextActionCode };
}

export interface PipelineStateView {
  correlation_id: string;
  stage: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  status: "pending" | "running" | "done" | "blocked" | "error";
  reason_code:
    | "extraction_deferred"
    | "extraction_failed"
    | "human_review_required"
    | "decision_failed"
    | "draft_failed"
    | "send_failed"
    | "superseded"
    | null;
}

/** #638: allowlisted bounded failure diagnostic — safe for every operator, never raw output. */
export interface PipelineDiagnosticView {
  code:
    | "ai_timeout"
    | "ai_unavailable"
    | "ai_provider_error"
    | "quota_exhausted"
    | "feature_disabled"
    | "db_constraint"
    | "db_query"
    | "contract_validation"
    | "send_not_eligible"
    | "unexpected";
  detail: string | null;
}

/** #540: the operator's "is the system working on this request?" projection. */
export interface AnalysisStateView {
  state: "idle" | "queued" | "running" | "ready" | "blocked" | "retryable_failed";
  stage: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | null;
  /** Automatic failure retries this chain has consumed (0–3). */
  attempt: number;
  reason_code: PipelineStateView["reason_code"];
  /** When the next automatic retry becomes eligible (queued-on-failure only). */
  retry_after: string | null;
  /** Set only on failure states (#638). */
  diagnostic?: PipelineDiagnosticView | null;
}

/** GET /api/tenant/health-alerts — owner-visible tenant health strip (#638). */
export interface TenantHealthAlerts {
  retryable_failed_count: number;
}

/** POST /api/requests/:id/analysis-retry — re-arm a terminally failed analysis (#540). */
export interface AnalysisRetryResult {
  ok: true;
  request_id: string;
  state: "queued";
  reason_code: PipelineStateView["reason_code"];
  retry_eligible_at: string;
  version: number;
}

export const ANALYSIS_RETRY_CONFLICT = "ANALYSIS_RETRY_NOT_AVAILABLE";

/** GET /api/requests/:id — full review context. */
export interface RequestDetail {
  fact_review?: import("../../../../core/src/review/fact-review-types").FactReview | null;
  send_readiness: import("../../../../core/src/review/send-readiness-types").SendReadiness | null;
  incoming_work?: IncomingWork | null;
  preparation: import("./workspace-view").WorkspacePreparation;
  workflow?: import("./workflow-types").WorkflowDetail | null;
  workflow_result: ReqventosWorkflowResultRef | null;
  request: {
    engine_pin: WorkflowEnginePin;
    workflow_unavailable_reason: "implementation_incomplete" | null;
    contact_form_source?: "gravity_forms" | null;
    confirmed_customer_email?: string | null;
    id: string;
    request_no?: number | null;
    reference?: string | null;
    status: string;
    lifecycle_status: RequestLifecycleStatus;
    lifecycle_changed_at?: string | null;
    opening_state?: "unopened" | "opened" | "legacy_unknown";
    current_revision: number;
    /** Optimistic-lock version of the request row; travels with Mark Done's undo (#535). */
    version?: number;
    assignees: Assignee[];
    reply_acknowledgement_token?: string | null;
    human_review_reason?: { code: string; question_count: number } | null;
    /** #677: this request was injected for testing, not received from a customer. */
    is_test?: boolean;
    /** #1787: in the trash since, and deleted for good at `purge_at`; null when not trashed. */
    trashed_at?: string | null;
    purge_at?: string | null;
    /** How long the trash keeps a request before deleting it for good (server policy). */
    trash_retention_days: number;
    message_deletions_pending?: number;
  };
  messages: MessageView[];
  decision: DecisionView | null;
  facts: Fact[];
  draft: DraftView | null;
  delivery: DeliveryView | null;
  delivery_failure?: DeliveryFailureView | null;
  pipeline?: PipelineStateView | null;
  analysis?: AnalysisStateView | null;
  review_guidance: ReviewGuidanceView;
  audit?: AuditEvent[];
  recipient_history?: AuditEvent[];
}

/** GET /api/requests/:id/customer — the tenant-scoped profile behind the trusted sender (#534). */
export interface CustomerView {
  id: string;
  /** Sender address as first seen; the read-only identity in v1. */
  email: string;
  normalized_sender_address: string;
  name: string | null;
  company: string | null;
  phone: string | null;
  notes: string | null;
  /** Per-field provenance (#588); a missing key means the field was never set. */
  field_sources: Partial<Record<CustomerProfileField, CustomerFieldSource>>;
  /**
   * Per-field value before the last operator change (#606); null means it was
   * empty. A missing key means the field has never been overwritten.
   */
  field_previous: Partial<Record<CustomerProfileField, string | null>>;
  first_contact_at: string | null;
  version: number;
}

export type CustomerProfileField = "name" | "company" | "phone" | "notes";
export type CustomerFieldSource = "extraction" | "operator";

/** A contact value the request offers for a field the profile does not hold (#588). */
export interface CustomerSuggestion {
  field: "name" | "company" | "phone";
  value: string;
  /** Verbatim mail snippet the value was read from, when extraction kept one. */
  evidence_text: string | null;
  /** Where the offered value came from — the mail, or an operator's own correction. */
  source: CustomerFieldSource;
}

export interface FormContactView {
  state: "unresolved" | "confirmed";
  source: "gravity_forms";
  source_address: string | null;
  suggested_email: string | null;
  source_message_id: string;
  source_hash: string;
  expected_version: number;
  expected_revision: number;
  expected_inbound_count: number;
  can_confirm: boolean;
}

export interface ConfirmFormCustomerInput {
  email: string;
  source_message_id: string;
  source_hash: string;
  expected_version: number;
  expected_revision: number;
  expected_inbound_count: number;
}

export interface CustomerForRequest {
  /** Null when the request has no server-trusted sender identity. */
  customer: CustomerView | null;
  reason: "untrusted_sender" | "form_customer_unconfirmed" | null;
  form_contact?: FormContactView;
  /** Empty when there is no profile or the mail adds nothing new (#588). */
  suggestions: CustomerSuggestion[];
}

export interface CustomerUpdateInput {
  name?: string | null;
  company?: string | null;
  phone?: string | null;
  notes?: string | null;
  expected_version: number;
}

export interface PositionItem {
  name: string;
  quantity: number | string;
  role?: string;
  bpId?: string;
  priceNet?: number | null;
  unit?: string | null;
  section?: "food" | "staff_equipment";
  staffing?: { role: string; headcount: number; durationHours: number };
}

export type PositionOccasion =
  | "kurzer_empfang"
  | "empfang_premium"
  | "lunch"
  | "flying_dinner"
  | "snack"
  | "dinner"
  | "premium";

export type PositionStaffingFormat =
  | "set_menu"
  | "flying"
  | "live_cooking_flying"
  | "buffet";

export interface PositionProposalOptions {
  occasion: PositionOccasion | null;
  staffingFormat: PositionStaffingFormat | null;
  durationHours: number | null;
  includeStaffing: boolean;
}

export type PositionResolutionCode =
  | "REVIEW_DIETARY_FACTS"
  | "REVIEW_REQUEST_FACTS"
  | "REVIEW_CATALOG"
  | "COMPLETE_PROPOSAL";

export interface PositionResolutionView {
  code: PositionResolutionCode;
  fact_fields?: string[];
}

/** GET /api/requests/:id/positions */
export interface PositionsView {
  engine?: "deterministic" | "single_agent";
  result_id?: string;
  commercial?: import("./workspace-view").WorkspaceProposal | null;
  alternatives?: import("./workspace-view").WorkspaceProposal[];
  scope?: ProposalScopeView;
  calculated_partial_total_net?: number;
  menu_preparation?: { request_revision: number; occasion: PositionOccasion | null; available_occasions: PositionOccasion[] };
  eligible: boolean;
  reason?: string | null;
  catering_type?: string;
  guest_count?: number;
  occasion?: PositionOccasion | null;
  available_occasions?: PositionOccasion[];
  quantity_ruleset_version?: string;
  quantity_per_person?: { value: number; unit: "pieces" | "grams" | "courses" };
  staffing_format?: PositionStaffingFormat | null;
  available_staffing_formats?: PositionStaffingFormat[];
  duration_hours?: number | null;
  include_staffing?: boolean;
  staffing_policy_version?: string;
  staffing_applied?: boolean;
  staffing_plan?: {
    format: PositionStaffingFormat;
    guestCount: number;
    source: "below_table" | "anchor" | "interpolated" | "extrapolated";
    headcounts: Record<string, number>;
  } | null;
  positions?: PositionItem[];
  estimated_food_total_net?: number | null;
  estimated_total_net?: number | null;
  commercial_outcome?:
    | "ONLINE_SHOP_ENTRY"
    | "INDIVIDUAL_OFFER"
    | "INDIVIDUAL_OFFER_PICKUP_ONLY"
    | "CONSULTATION"
    | "DEFER";
  commercial_reason_code?: string;
  commercial_threshold_net_eur?: number | null;
  extras_eligibility?: "ELIGIBLE" | "INELIGIBLE" | "UNRESOLVED";
  extras_reason_code?:
    | "MEETS_EXTRAS_MINIMUM"
    | "BELOW_EXTRAS_MINIMUM"
    | "FOOD_SUBTOTAL_UNAVAILABLE"
    | "FACTS_REQUIRE_REVIEW";
  extras_threshold_net_eur?: number;
  commercial_block?: "CONSULTATION" | "DEFER" | "OUTCOME_MISMATCH" | "CHANGE_FORBIDDEN";
  diagnostics?: string[];
  needs_enrichment?: string[];
  resolution?: PositionResolutionView;
  bp_preview?: { content: string } | null;
}

/** GET /api/users (admin) */
export interface UserRow {
  id: string;
  email: string;
  /** Display name when the operator set one; the picker falls back to the email. */
  name?: string | null;
  role: string;
  createdAt?: string | null;
}

/** One member of a work team (#802) — a staff row, not a separate identity. */
export interface TeamMemberRow {
  id: string;
  email: string;
  name?: string | null;
  role: string;
}

/**
 * A tenant work team (#802). `members` is the CURRENT membership, resolved on every read: there
 * is no snapshot anywhere, so what the screen shows is what downstream authorization will see.
 * `archivedAt` non-null means withdrawn from new use while still fully resolvable.
 */
export interface TeamRow {
  id: string;
  name: string;
  archivedAt?: string | null;
  version: number;
  createdAt?: string | null;
  members: TeamMemberRow[];
}

export type DraftStatus =
  | "drafted"
  | "skipped_human_review"
  | "no_decision"
  | "ai_error"
  | "already_generating"
  | "skipped_existing_draft"
  | "skipped_delivery"
  | "skipped_commercial_defer";
export type DraftContinuationStatus = "stale" | DraftStatus | "not_draftable" | "unchanged";
export interface DraftResult {
  status: DraftStatus;
  error?: string;
}
export interface RecomputeDecisionResult {
  ok: true;
  request_id: string;
  decision_id: string;
  draft_status: DraftContinuationStatus;
  reload: true;
  regeneration_required: true;
}
export interface OverrideFactResult {
  ok: true;
  draft_status: DraftContinuationStatus;
}
export interface ReplyAcknowledgementResult {
  status: "acknowledged" | "already_acknowledged" | "newer_reply_available";
}
export interface CloseRequestResult {
  ok: true;
  request_id: string;
  status: "closed";
  lifecycle_status?: RequestLifecycleStatus;
  /** The request version after Mark Done — what an undo must be able to prove it acted on. */
  version: number;
}

/** POST /api/requests/:id/undo-done — the reverse of Mark Done (#535). */
export interface UndoDoneResult {
  ok: true;
  request_id: string;
  status: string;
  lifecycle_status: RequestLifecycleStatus;
  version: number;
}

export const REQUEST_DONE_UNDO_CONFLICT = "REQUEST_DONE_UNDO_CONFLICT";

/** POST /api/requests/:id/trash — set a request aside; it is deleted for good at `purge_at`. */
export interface TrashRequestResult {
  ok: true;
  request_id: string;
  status: string;
  version: number;
  trashed_at: string;
  purge_at: string;
}

/** POST /api/requests/:id/restore — back out of the trash, into the state it was trashed from. */
export interface RestoreRequestResult {
  ok: true;
  request_id: string;
  status: string;
  version: number;
}

/** Any work on a request that is in the trash is refused with this code (#1787). */
export const REQUEST_IN_TRASH = "REQUEST_IN_TRASH";
export const REQUEST_TRASH_CONFLICT = "REQUEST_TRASH_CONFLICT";

/** Why the server refused a move to the trash or a restore. */
export type RequestTrashRefusal =
  | "already_in_trash"
  | "not_in_trash"
  | "version_mismatch"
  | "delivery_in_progress"
  | "delivery_uncertain"
  | "submission_pending"
  | "filed"
  | "email_qa_run"
  | "purge_due"
  | "message_deletion_pending";

/** Why the server refused an undo. Every reason means the request is no longer the one that was
 *  marked done, so the console explains instead of retrying. */
export type UndoDoneRefusal =
  | "not_done"
  | "archived"
  | "version_mismatch"
  | "missing_prior_state";

export interface UndoDoneConflict {
  code: typeof REQUEST_DONE_UNDO_CONFLICT;
  reason: UndoDoneRefusal;
  request_id: string;
  current_version: number;
  lifecycle_status: RequestLifecycleStatus | null;
}
export interface SendResult {
  status: "sent" | "already_sent" | "needs_reconcile" | "failed" | "delivery_uncertain" | "pending";
  operationId?: string;
  errorCode?: string;
  messageId?: string;
  error?: string;
}

export interface ApproveAndSendCommand {
  idempotency_key: string;
  expected_version: number;
  expected_request_revision: number;
  expected_artifact_hash: string;
  /** #2516: the open tasks the send confirmation listed for a reply without an offer. */
  confirm_task_ids?: string[];
}

/** #278: per-tenant customer email signature (legal Impressum block). */
export interface EmailSignatureValue {
  text_de: string;
  html_de: string;
  logo_id?: string | null;
}

export interface EmailSignatureLogo {
  id: string;
  content_id: string;
  content_type: string;
  byte_size: number;
  width: number;
  height: number;
  preview_url: string;
}

export interface EmailSignatureState {
  /** The tenant's stored value, or null when the tenant has not set one. */
  value: EmailSignatureValue | null;
  /** What generated drafts actually carry after fallback resolution. */
  effective: EmailSignatureValue;
  source: "tenant" | "default" | "none";
  logo?: EmailSignatureLogo | null;
}

/** Mail that arrived at the published address and was not an enquiry (#794). */
export type FiledReason =
  | "bulk"
  | "auto_submitted"
  | "null_return_path"
  | "unauthenticated"
  /** Our own mail server's spam score, not a header the sender wrote (#934). */
  | "spam_score"
  | "model_non_enquiry";

export interface FiledMessageSummary {
  id: string;
  subject: string | null;
  fromAddr: string | null;
  receivedAt: string | null;
  filedReason: FiledReason;
  firstOpenedAt: string | null;
  promotedRequestId: string | null;
  promotedAt: string | null;
  /** #2433: set while the item is in the trash, with the day it is deleted for good. */
  trashedAt: string | null;
  purgeAt: string | null;
}

export interface FiledMessageDetail extends FiledMessageSummary {
  toAddrs: string[];
  normalizedText: string | null;
  /** The header name and value that decided it — the evidence, shown to the operator verbatim. */
  filedSignals: Array<{ header: string; value: string }>;
}

export interface FiledMessageList {
  /** Still set aside, whether read or not. */
  open: number;
  /** Still set aside and not yet opened by the team. */
  unread: number;
  /** Everything filed, promoted or not, except what is in the trash. */
  total: number;
  /** #2433: in the trash. */
  trash: number;
  /** How long an item stays restorable in the trash. */
  trashRetentionDays: number;
  items: FiledMessageSummary[];
}

/** #2433: which Filed list to read. */
export type FiledMessageScope = "open" | "all" | "trash";

/** #2433: refusal of a Filed trash move, restore or promotion, with its reason. */
export const FILED_MESSAGE_TRASH_CONFLICT = "FILED_MESSAGE_TRASH_CONFLICT";

export type FiledTrashRefusal = "already_in_trash" | "not_in_trash" | "promoted" | "purge_due";

export interface FiledTrashResult {
  filedMessageId: string;
  trashedAt: string | null;
  purgeAt: string | null;
}

export interface FiledMessagePromotion {
  filedMessageId: string;
  requestId: string;
  alreadyPromoted: boolean;
}

/**
 * #811: the Overview's operational numbers for one period, computed on the server.
 *
 * Every ratio arrives with the denominator it was taken over, and an unanswerable metric arrives
 * as `null` rather than zero — the console must be able to say "nothing is settled yet" without
 * inventing a nought per cent. Nothing here may be recomputed from the paginated Inbox.
 */
export type OverviewPeriod = "day" | "week" | "month" | "custom";

export interface OverviewMetrics {
  period: OverviewPeriod;
  /** Inclusive calendar bounds in `time_zone` — the business's dates, never the browser's. */
  date_from: string;
  date_to: string;
  time_zone: string;
  new_enquiries: number;
  /** Share of settled enquiries first processed inside 24 hours; null when none is settled. */
  processed_within_24h_ratio: number | null;
  processed_within_24h: number;
  processed_within_24h_denominator: number;
  delayed_now: number;
  average_guest_count: number | null;
  average_guest_count_denominator: number;
  /** When the server computed this, so the console can say how old it is rather than guess. */
  computed_at: string;
}

export interface OverviewMetricsQuery {
  period: OverviewPeriod;
  from?: string;
  to?: string;
}

/**
 * #812: who else is in the Cockpit for this business.
 *
 * `online` and `offline` are the server's only two answers, because they are the only two it can
 * know. `unknown` is the console's word for "the server did not answer" and never arrives over
 * the wire — showing a colleague as offline because a request failed would be a false statement
 * about a person.
 *
 * No member carries a timestamp. The product does not record when somebody was at their desk, so
 * there is nothing here for an interface to accumulate into a record of their day.
 */
export type PresenceState = "online" | "offline";

export interface PresenceMember {
  user_id: string;
  name: string | null;
  email: string;
  role: string;
  state: PresenceState;
}

export interface PresenceView {
  /** The window the states were computed against, so the console need not assume it. */
  ttl_seconds: number;
  /** How often to refresh, told by the server rather than hard-coded on both sides. */
  heartbeat_seconds: number;
  members: PresenceMember[];
  online_count: number;
  computed_at: string;
}

/** Source guest-count representation; the server owns effective counts and business policy. */
export type GuestCountValue =
  | { version: 1; kind: "exact" | "estimate"; value: number }
  | { version: 1; kind: "range"; lower: number; upper: number; approximate: boolean }
  | { version: 1; kind: "lower_bound" | "upper_bound"; value: number; inclusive: boolean };

/** Safe operational receipt; the server never returns receiving identities or credentials. */
export interface MailboxRefreshView {
  operationId: string | null;
  status: "idle" | "queued" | "running" | "completed" | "partial" | "failed" | "unavailable" | "deferred";
  reason: string | null;
  checked: number;
  failed: number;
  newMessages: number;
  retryAfter: string | null;
}
